import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { canhBaoRepository } from '../../../lib/repositories/canh-bao.repository';
import { useToast } from '../../../lib/toast';
import { thuLai } from '../../vai-tro/hooks/useVaiTro';
import { etagTuPhienBan } from '../nghiep-vu/canh-bao';
import type {
  LocBanGhi,
  MaKenh,
  NotifyAlarmTask,
  NotifyAlarmTaskWrite,
  NotifyCatalogPatch,
  NotifyChannel,
  NotifyChannelCreate,
  NotifyChannelUpdate,
  NotifyTestResult,
  PhamViCanhBao,
  TelegramChat,
  TelegramChatsRequest,
  Trang,
} from '../types';

export const KHOA_CANH_BAO = {
  goc: ['canh-bao'] as const,
  danhMuc: (pv: PhamViCanhBao) => ['canh-bao', pv, 'danh-muc'] as const,
  kenh: (pv: PhamViCanhBao) => ['canh-bao', pv, 'kenh'] as const,
  tacVu: (pv: PhamViCanhBao) => ['canh-bao', pv, 'tac-vu'] as const,
  banGhi: (pv: PhamViCanhBao) => ['canh-bao', pv, 'ban-ghi'] as const,
  suDung: ['canh-bao', 'tenant', 'su-dung'] as const,
  danhMucNenTang: ['canh-bao', 'nen-tang', 'danh-muc-day-du'] as const,
  tongQuan: ['canh-bao', 'nen-tang', 'tong-quan'] as const,
};

/** Trần số trang khi nối: chặn vòng lặp nếu server trả `next_cursor` lặp lại. */
const TRANG_TOI_DA = 20;

/** Nối hết các trang: cấu hình kênh tối đa 20, tác vụ vài chục — lọc và gom ở client. */
async function layHet<T>(lay: (cursor?: string) => Promise<Trang<T>>): Promise<T[]> {
  const ra: T[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < TRANG_TOI_DA; i += 1) {
    const trang = await lay(cursor);
    ra.push(...trang.items);
    cursor = trang.next_cursor ?? undefined;
    if (!cursor) break;
  }
  return ra;
}

export function useDanhMuc(pv: PhamViCanhBao) {
  return useQuery({
    queryKey: KHOA_CANH_BAO.danhMuc(pv),
    queryFn: () => canhBaoRepository.danhMuc(pv),
    staleTime: 5 * 60_000,
    retry: thuLai,
  });
}

export function useDanhSachKenh(pv: PhamViCanhBao) {
  return useQuery<NotifyChannel[], ApiError>({
    queryKey: KHOA_CANH_BAO.kenh(pv),
    queryFn: () => layHet((c) => canhBaoRepository.danhSachKenh(pv, c)),
    staleTime: 30_000,
    retry: thuLai,
  });
}

export function useDanhSachTacVu(pv: PhamViCanhBao) {
  return useQuery<NotifyAlarmTask[], ApiError>({
    queryKey: KHOA_CANH_BAO.tacVu(pv),
    queryFn: () => layHet((c) => canhBaoRepository.danhSachTacVu(pv, c)),
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Nhật ký phân trang con trỏ, "Tải thêm" ở cuối bảng. */
export function useNhatKy(pv: PhamViCanhBao, loc: LocBanGhi) {
  return useInfiniteQuery({
    queryKey: [...KHOA_CANH_BAO.banGhi(pv), loc] as const,
    queryFn: ({ pageParam }) => canhBaoRepository.danhSachBanGhi(pv, loc, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (trang) => trang.next_cursor ?? undefined,
    staleTime: 15_000,
    retry: thuLai,
  });
}

export function useChiTietBanGhi(pv: PhamViCanhBao, id: string | null) {
  return useQuery({
    queryKey: [...KHOA_CANH_BAO.banGhi(pv), 'chi-tiet', id] as const,
    queryFn: () => canhBaoRepository.chiTietBanGhi(pv, id!),
    enabled: id !== null,
    retry: thuLai,
  });
}

export function useSuDung(bat: boolean) {
  return useQuery({
    queryKey: KHOA_CANH_BAO.suDung,
    queryFn: () => canhBaoRepository.suDung(),
    enabled: bat,
    staleTime: 60_000,
    retry: thuLai,
  });
}

export function useDanhMucNenTang() {
  return useQuery({
    queryKey: KHOA_CANH_BAO.danhMucNenTang,
    queryFn: () => canhBaoRepository.danhMucNenTang(),
    retry: thuLai,
  });
}

export function useTongQuan() {
  return useQuery({
    queryKey: KHOA_CANH_BAO.tongQuan,
    queryFn: () => canhBaoRepository.tongQuan(),
    staleTime: 30_000,
    retry: thuLai,
  });
}

export type LuuKenh =
  | { tao: true; du: NotifyChannelCreate; khoaLap: string }
  | { tao: false; kenh: NotifyChannel; du: NotifyChannelUpdate };

/** Thêm hoặc sửa cấu hình kênh. Sửa gửi `If-Match` theo phiên bản đã đọc; `412` thì tải lại danh sách. */
export function useLuuKenh(pv: PhamViCanhBao) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<NotifyChannel, ApiError, LuuKenh>({
    mutationFn: (v) =>
      v.tao
        ? canhBaoRepository.taoKenh(pv, v.du, v.khoaLap)
        : canhBaoRepository.suaKenh(pv, v.kenh.id, etagTuPhienBan(v.kenh.version), v.du),
    onSuccess: (k, v) => {
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.kenh(pv) });
      toast.success(t(v.tao ? 'canhBaoKenh.daThem' : 'canhBaoKenh.daLuu', { ten: k.name }));
    },
    onError: (err) => {
      if (err.status === 412 || err.status === 404) void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.kenh(pv) });
    },
  });
}

/** Bật, tắt một cấu hình: `PUT` lại tên và `config` đang có, secret không gửi nên giữ nguyên. */
export function useBatTatKenh(pv: PhamViCanhBao) {
  const qc = useQueryClient();
  const toast = useToast();
  const dichLoi = useDichLoiToast();
  const { t } = useT();
  return useMutation<NotifyChannel, ApiError, NotifyChannel>({
    mutationFn: (k) =>
      canhBaoRepository.suaKenh(pv, k.id, etagTuPhienBan(k.version), {
        name: k.name,
        config: k.config,
        enabled: !k.enabled,
      }),
    onSuccess: (k) => {
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.kenh(pv) });
      toast.success(
        t(k.enabled ? 'canhBaoKenh.daBat' : 'canhBaoKenh.daTat', {
          ten: k.name,
        }),
      );
    },
    onError: (err) => {
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.kenh(pv) });
      dichLoi(err);
    },
  });
}

export function useXoaKenh(pv: PhamViCanhBao) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<void, ApiError, NotifyChannel>({
    mutationFn: (k) => canhBaoRepository.xoaKenh(pv, k.id),
    onSuccess: (_kq, k) => {
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.kenh(pv) });
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.tacVu(pv) });
      toast.success(t('canhBaoKenh.daGo', { ten: k.name }));
    },
    onError: (err) => {
      if (err.status === 404) void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.kenh(pv) });
    },
  });
}

/** Gửi thử: kết quả hiện bằng toast — thành công hay lỗi đã che secret của provider. */
export function useGuiThu(pv: PhamViCanhBao) {
  const qc = useQueryClient();
  const toast = useToast();
  const dichLoi = useDichLoiToast();
  const { t } = useT();
  return useMutation<NotifyTestResult, ApiError, NotifyChannel>({
    mutationFn: (k) => canhBaoRepository.guiThu(pv, k.id),
    onSuccess: (kq, k) => {
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.kenh(pv) });
      if (kq.ok) toast.success(t('canhBaoKenh.guiThuDuoc', { ten: k.name }));
      else
        toast.error(
          t('canhBaoKenh.guiThuHong', {
            ten: k.name,
            loi: kq.message ?? kq.error_code ?? '',
          }),
        );
    },
    onError: dichLoi,
  });
}

export function useChatTelegram() {
  return useMutation<TelegramChat[], ApiError, TelegramChatsRequest>({
    mutationFn: (du) => canhBaoRepository.chatTelegram(du),
  });
}

export type LuuTacVu =
  | { tao: true; du: NotifyAlarmTaskWrite; khoaLap: string }
  | { tao: false; tacVu: NotifyAlarmTask; du: NotifyAlarmTaskWrite };

export function useLuuTacVu(pv: PhamViCanhBao) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<NotifyAlarmTask, ApiError, LuuTacVu>({
    mutationFn: (v) =>
      v.tao
        ? canhBaoRepository.taoTacVu(pv, v.du, v.khoaLap)
        : canhBaoRepository.suaTacVu(pv, v.tacVu.id, etagTuPhienBan(v.tacVu.version), v.du),
    onSuccess: (tv, v) => {
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.tacVu(pv) });
      toast.success(
        t(v.tao ? 'canhBao.daThemTacVu' : 'canhBao.daLuuTacVu', {
          ten: tv.title,
        }),
      );
    },
    onError: (err) => {
      if (err.status === 412 || err.status === 404) void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.tacVu(pv) });
    },
  });
}

/** `NotifyAlarmTaskWrite` của một tác vụ đang có — để bật, tắt mà không đổi gì khác. */
export function banGhiTuTacVu(tv: NotifyAlarmTask): NotifyAlarmTaskWrite {
  return {
    type_code: tv.type_code,
    title: tv.title,
    params: tv.params,
    channel_ids: tv.channel_ids,
    daily_limit: tv.daily_limit ?? null,
    total_limit: tv.total_limit ?? null,
    min_interval_sec: tv.min_interval_sec,
    send_from: tv.send_from,
    send_to: tv.send_to,
    enabled: tv.enabled,
  };
}

export function useBatTatTacVu(pv: PhamViCanhBao) {
  const qc = useQueryClient();
  const toast = useToast();
  const dichLoi = useDichLoiToast();
  const { t } = useT();
  return useMutation<NotifyAlarmTask, ApiError, NotifyAlarmTask>({
    mutationFn: (tv) =>
      canhBaoRepository.suaTacVu(pv, tv.id, etagTuPhienBan(tv.version), {
        ...banGhiTuTacVu(tv),
        enabled: !tv.enabled,
      }),
    onSuccess: (tv) => {
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.tacVu(pv) });
      toast.success(
        t(tv.enabled ? 'canhBao.daBatTacVu' : 'canhBao.daTatTacVu', {
          ten: tv.title,
        }),
      );
    },
    onError: (err) => {
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.tacVu(pv) });
      dichLoi(err);
    },
  });
}

export function useXoaTacVu(pv: PhamViCanhBao) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<void, ApiError, NotifyAlarmTask>({
    mutationFn: (tv) => canhBaoRepository.xoaTacVu(pv, tv.id),
    onSuccess: (_kq, tv) => {
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.tacVu(pv) });
      toast.success(t('canhBao.daXoaTacVu', { ten: tv.title }));
    },
    onError: (err) => {
      if (err.status === 404) void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.tacVu(pv) });
    },
  });
}

export function useGuiLai(pv: PhamViCanhBao) {
  const qc = useQueryClient();
  const toast = useToast();
  const dichLoi = useDichLoiToast();
  const { t } = useT();
  return useMutation<unknown, ApiError, { deliveryId: string; khoaLap: string }>({
    mutationFn: (v) => canhBaoRepository.guiLai(pv, v.deliveryId, v.khoaLap),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.banGhi(pv) });
      toast.success(t('canhBaoNhatKy.daXepGuiLai'));
    },
    onError: dichLoi,
  });
}

export function useSuaDanhMuc() {
  const qc = useQueryClient();
  const toast = useToast();
  const dichLoi = useDichLoiToast();
  const { t } = useT();
  return useMutation<
    unknown,
    ApiError,
    { loai: 'kenh'; code: MaKenh; du: NotifyCatalogPatch } | { loai: 'canh-bao'; code: string; du: NotifyCatalogPatch }
  >({
    mutationFn: (v) =>
      v.loai === 'kenh' ? canhBaoRepository.suaLoaiKenh(v.code, v.du) : canhBaoRepository.suaLoaiCanhBao(v.code, v.du),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.goc });
      toast.success(t('canhBaoNenTang.daLuuDanhMuc'));
    },
    onError: (err) => {
      void qc.invalidateQueries({ queryKey: KHOA_CANH_BAO.danhMucNenTang });
      dichLoi(err);
    },
  });
}

/** Lỗi của thao tác nhanh (không có hộp để hiện) đi ra toast. */
function useDichLoiToast() {
  const toast = useToast();
  const dichLoi = useLoi();
  return (err: ApiError) => {
    toast.error(dichLoi(err));
  };
}
